import Decimal from "decimal.js"

export type PositiveAmountResult =
	| { amount: number; error?: never }
	| { amount?: never; error: string }

export type NonNegativeAmountResult = PositiveAmountResult

const parseAmountToMinorUnits = (
	value: unknown,
	allowZero: boolean,
	minorUnits: number
): PositiveAmountResult => {
	try {
		const decimal = new Decimal(String(value ?? "").trim())
		const isOutsideRange = allowZero ? decimal.lt(0) : decimal.lte(0)
		if (!decimal.isFinite() || isOutsideRange) {
			return {
				error: allowZero
					? "Amount must be zero or greater."
					: "Amount must be greater than zero."
			}
		}

		const amount = decimal.mul(minorUnits)
		if (!amount.isInteger()) {
			return { error: "Amount can have at most two decimal places." }
		}

		const asNumber = amount.toNumber()
		if (!Number.isSafeInteger(asNumber)) {
			return { error: "Amount is too large." }
		}

		return { amount: asNumber }
	} catch {
		return { error: "Enter a valid amount." }
	}
}

export const parsePositiveAmountToMinorUnits = (
	value: unknown,
	minorUnits = 100
): PositiveAmountResult => parseAmountToMinorUnits(value, false, minorUnits)

export const parseNonNegativeAmountToMinorUnits = (
	value: unknown,
	minorUnits = 100
): NonNegativeAmountResult => parseAmountToMinorUnits(value, true, minorUnits)

export const amountValidationError = (message: string): IAPIResponse<never> => ({
	error: { code: 0, message }
})

// Mirrors the backend recharge fee: round(amount * percent / 10000) + fixed, percent in ×100 units.
export const topUpFeeMinorUnits = (amount: number, percent: number, fixed: number): number =>
	amount > 0 ? new Decimal(amount).mul(percent).div(10000).round().toNumber() + fixed : 0

// Largest amount whose amount + fee still fits in the balance.
// ponytail: ignores per-company min/max fee caps the backend may apply; the server still has the final say.
export const maxTopUpMinorUnits = (balance: number, percent: number, fixed: number): number => {
	let amount = Math.max(0, new Decimal(balance - fixed).mul(10000).div(10000 + percent).floor().toNumber())
	while (amount > 0 && amount + topUpFeeMinorUnits(amount, percent, fixed) > balance) amount--
	while (amount + 1 + topUpFeeMinorUnits(amount + 1, percent, fixed) <= balance) amount++
	return amount
}
